import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { fetchDecodedPeaks, getCachedDecoded, setCachedDecoded, downsamplePeaks } from '../utils/peaksCache';
import './WaveformOverlay.scss';

// The loader stays up until the peaks fetch SETTLES — fetchDecodedPeaks can
// no longer hang (download stall-watchdog + decode timeout + never-reject),
// so there's no need for the old 20s reveal timer, which hid the loader on a
// clock and left a visible empty gap whenever peaks landed moments later.
// Seeking during load still works: the loader is pointer-events: none, so
// clicks fall through to the (invisible) container's seek handler.

// Design v2: played = amber (--color-accent), remaining = --color-text-tertiary
// at 45%. Read from the live tokens so a theme switch stays in step; null when
// the flag is off (classic cyan).
function v2WaveColors() {
    if (typeof document === 'undefined') return null;
    const root = document.documentElement;
    if (root.getAttribute('data-design') !== 'v2') return null;
    const css = getComputedStyle(root);
    const accent = (css.getPropertyValue('--color-accent') || '').trim() || '#ffb347';
    const tertiary = (css.getPropertyValue('--color-text-tertiary') || '').trim() || '#7a736a';
    const alpha = (hex, a) => {
        const h = hex.replace('#', '');
        if (!/^[0-9a-f]{6}$/i.test(h)) return hex;
        const n = parseInt(h, 16);
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
    };
    const top = (css.getPropertyValue('--v2-wave-played-top') || '').trim();
    const bottom = (css.getPropertyValue('--v2-wave-played-bottom') || '').trim();
    let played = accent;
    if (top && bottom) {
        // wavesurfer 7 takes a colour-stop array for a vertical gradient.
        played = [top, bottom];
    }
    // Opaque unplayed grey (the --v2-wave token): a translucent one made the
    // played amber translucent too, since wavesurfer recolours these pixels.
    const unplayed = (css.getPropertyValue('--v2-wave') || '').trim();
    return { waveColor: unplayed || alpha(tertiary, 0.45), progressColor: played, cursorColor: accent };
}

const WaveformOverlay = ({ audioElement, sourceUrl, originalUrl }) => {
    const waveformRef = useRef(null);
    const wavesurferRef = useRef(null);
    const timeoutRef = useRef(null);
    const audioElRef = useRef(audioElement);
    const peakDurationRef = useRef(null);
    const [ready, setReady] = useState(false);

    // Keep ref in sync but don't trigger effect
    audioElRef.current = audioElement;

    // Explicit click-to-seek: for HLS mix streams, WaveSurfer's internal
    // click handler maps click ratio through its own duration model, which
    // doesn't always propagate to the attached media element via hls.js.
    // Computing currentTime here and setting it on the media element is
    // more reliable. Falls back to the wavesurfer-provided peak duration
    // when audio.duration isn't finite yet.
    const handleClick = (e) => {
        const audio = audioElRef.current;
        const container = waveformRef.current;
        if (!audio || !container) return;
        const rect = container.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const dur = Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : peakDurationRef.current;
        if (!dur || !Number.isFinite(dur)) return;
        try {
            audio.currentTime = ratio * dur;
            if (wavesurferRef.current) wavesurferRef.current.seekTo(ratio);
        } catch (err) { /* ignore seek errors */ }
    };

    // Theme change: repaint with the new theme's palette (the feed rows do the
    // same). Without it the player kept the old theme's ink after a switch.
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const ws = wavesurferRef.current;
            const c = v2WaveColors();
            if (ws && c) ws.setOptions({ waveColor: c.waveColor, progressColor: c.progressColor, cursorColor: c.cursorColor });
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!waveformRef.current || !audioElRef.current) return;

        setReady(false);
        let cancelled = false;
        const audio = audioElRef.current;

        const v2 = v2WaveColors();
        const baseOptions = {
            waveColor: v2 ? v2.waveColor : '#4a5568',
            progressColor: v2 ? v2.progressColor : '#00bcd4',
            cursorColor: v2 ? v2.cursorColor : '#00bcd4',
            cursorWidth: 2,
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            // Design v2's 96px bar gives the wave 60px; classic keeps 50.
            height: v2 ? 60 : 50,
            normalize: true,
            backend: 'MediaElement',
            media: audio,
            // Click-to-seek is handled by our own container onClick handler
            // (more reliable for HLS streams attached via hls.js).
            interact: false,
            hideScrollbar: true,
        };

        const createWs = (extra) => {
            if (cancelled || !waveformRef.current) return null;
            try {
                const ws = WaveSurfer.create({
                    container: waveformRef.current,
                    ...baseOptions,
                    ...(extra || {}),
                });
                // Watchdog: reveal whatever's rendered only after a long stretch
                // with NO load/decode progress. It resets on every progress
                // event, so a slow-but-advancing decode (a 2h mix takes ~15s)
                // keeps the loader up. This replaces the old blind 60s timer
                // that hid the loader mid-load and left an empty gap before the
                // wave actually painted.
                const armWatchdog = () => {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => {
                        if (!cancelled) setReady(true);
                    }, 30000);
                };
                ['loading', 'decode', 'redraw'].forEach((ev) => ws.on(ev, armWatchdog));
                armWatchdog();
                const reveal = () => {
                    clearTimeout(timeoutRef.current);
                    if (!cancelled) setReady(true);
                };
                ws.on('ready', reveal);
                // With pre-computed peaks the bars are painted on the first
                // 'redraw', but the MediaElement-backed 'ready' can trail the
                // media's own readiness — which made the feed card (no media,
                // instant ready) show its wave visibly BEFORE the player's.
                // Reveal on whichever fires first.
                ws.on('redraw', reveal);
                ws.on('error', (err) => {
                    console.warn('[wf] ws:error → setReady(true)', err);
                    clearTimeout(timeoutRef.current);
                    if (!cancelled) setReady(true);
                });
                ws.setMuted(false);
                wavesurferRef.current = ws;
                return ws;
            } catch (err) {
                console.warn('[wf] createWs threw → setReady(true)', err);
                if (!cancelled) setReady(true);
                return null;
            }
        };

        // Peaks come from the shared cache via the original URL — the SAME key
        // the feed-row waveforms (MixFeedWaveform / FeedWaveform) use, so the
        // player and the feed row for a track/mix share one download+decode and
        // load in lockstep (whichever finishes first populates the cache for
        // the other). The loader stays up (ready=false) the whole time; ws
        // 'ready' clears it — so there's no empty gap between loader and wave.
        // Sample clip (~30s) — decode it OURSELVES and hand WaveSurfer
        // pre-computed peaks (cache shared with FeedWaveform, keyed by the
        // clip URL).
        //
        // NEVER create a bare no-peaks WaveSurfer here: with only
        // `media` it async-fetches the clip and then REWRITES the
        // shared <audio>'s src with the URL it captured at init.
        // This overlay mounts during onAudioPlayTrackChange — BEFORE
        // jinke swaps audio.src — so on every queue switch it
        // captured the PREVIOUS track's URL and clobbered the new
        // one mid-play (audible as every playlist row replaying the
        // first track's sample while the UI showed the right title).
        // With peaks+duration provided, WaveSurfer skips its fetch
        // and its setSrc is a same-URL no-op.
        const decodeSampleClip = async (clipUrl) => {
            try {
                let entry = getCachedDecoded(clipUrl);
                if (!entry) {
                    const resp = await fetch(clipUrl);
                    const buf = await resp.arrayBuffer();
                    // OfflineAudioContext: decode-only work must not
                    // consume one of Chromium's ~6 hardware-context
                    // slots (see peaksCache._decodeAndCache).
                    const audioCtx = new OfflineAudioContext(1, 8000, 8000);
                    const decoded = await audioCtx.decodeAudioData(buf);
                    if (cancelled || !waveformRef.current) return;
                    entry = { peaks: downsamplePeaks(decoded.getChannelData(0)), duration: decoded.duration };
                    setCachedDecoded(clipUrl, entry);
                }
                if (cancelled || !waveformRef.current) return;
                peakDurationRef.current = entry.duration || null;
                createWs({ peaks: [entry.peaks], duration: entry.duration });
            } catch (err) {
                // Decode failed (network/codec) — reveal the
                // click-seekable area like the peaks-unavailable branch.
                console.warn('[wf] sample decode failed → setReady(true)', err);
                setReady(true);
            }
        };

        // A /lazy-audio entry names its own sample fallback in the URL. When
        // the audio server reports it actually 302'd to that sample (no full
        // stream resolved — now, or ever), there are no peaks to fetch and the
        // playing audio IS the ~30s clip: decode it, like any sample play.
        // Without this the player showed a permanently blank seek strip for
        // playlist rows with no resolvable full stream.
        const lazyFallbackUrl = (() => {
            if (!(sourceUrl || '').includes('/lazy-audio')) return null;
            try { return new URL(sourceUrl).searchParams.get('fallback'); } catch { return null; }
        })();
        let offLazyFallback = null;

        // Direct-play entry whose stream died mid-flight and was swapped to
        // its Apple sample by onAudioError (player-sample-fallback). The peaks
        // fetch below is for the FULL track and either fails (the source is
        // what died) or, worse, lands minutes later and paints a 4-minute
        // wave under a 30-second clip. Take over: drop whatever's drawn and
        // decode the clip that is actually playing. `sampleTakenOver` makes a
        // late peaks result a no-op. On (re)mount, replay from the Map — but
        // only when the element is really on that sample, so a later
        // successful play of the same entry (identical musicSrc) isn't
        // mistaken for the fallback.
        let sampleTakenOver = false;
        const takeOverWithSample = (sampleUrl) => {
            if (cancelled || sampleTakenOver || !sampleUrl) return;
            sampleTakenOver = true;
            console.log('[wf] stream fell back to sample — decoding the clip instead of full-track peaks');
            clearTimeout(timeoutRef.current);
            if (wavesurferRef.current) {
                try { wavesurferRef.current.unAll(); wavesurferRef.current.destroy(); } catch (_) { /* ignore */ }
                wavesurferRef.current = null;
            }
            setReady(false);
            decodeSampleClip(sampleUrl);
        };
        const onSampleFallback = (e) => {
            if (e?.detail?.musicSrc !== sourceUrl) return;
            takeOverWithSample(e.detail.sampleUrl);
        };
        window.addEventListener('player-sample-fallback', onSampleFallback);
        const replaySample = window.__sampleFallbacks?.get(sourceUrl);
        if (replaySample && audio.src === replaySample) {
            takeOverWithSample(replaySample);
        }

        (async () => {
            if (sampleTakenOver) return;
            let entry = null;
            if (originalUrl) {
                // Priority: this is the CURRENTLY PLAYING track's
                // waveform — it must not queue behind prefetches. The await
                // always settles (see header comment), so the loader can
                // honestly stay up for the whole download+decode without a
                // reveal-timer hedge — and without the empty gap the timer
                // caused when peaks landed just after it fired.
                entry = await fetchDecodedPeaks(originalUrl, { priority: true });
            }
            if (cancelled || !waveformRef.current || sampleTakenOver) return;
            if (entry) {
                peakDurationRef.current = entry.duration || null;
                createWs({ peaks: [entry.peaks], duration: entry.duration });
            } else if (lazyFallbackUrl) {
                if (window.__lazyAudioFallbacks?.has(lazyFallbackUrl)) {
                    await decodeSampleClip(lazyFallbackUrl);
                } else {
                    // Fallback not (yet) reported. Show the click-seekable
                    // area; if the server falls back mid-resolve, draw the
                    // sample then. A successful resolve instead flips
                    // originalUrl (lazy-audio-resolved) and re-runs this
                    // effect with real peaks.
                    setReady(true);
                    const onFallback = (e) => {
                        if (e?.detail?.fallback !== lazyFallbackUrl || cancelled) return;
                        window.removeEventListener('lazy-audio-fallback', onFallback);
                        offLazyFallback = null;
                        decodeSampleClip(lazyFallbackUrl);
                    };
                    offLazyFallback = onFallback;
                    window.addEventListener('lazy-audio-fallback', onFallback);
                }
            } else if (!originalUrl) {
                if ((sourceUrl || '').includes('/lazy-audio')) {
                    // Full-track lazy entry whose link is still being resolved
                    // server-side (and no sample fallback to fall to): do NOT
                    // let WaveSurfer decode the live stream — that's a FULL
                    // track and took 1-2 minutes (the "waveform appeared at
                    // 1:20" bug). The resolved link arrives via
                    // lazy-audio-resolved → originalUrl flips → this effect
                    // re-runs with real peaks. Show the click-seekable area
                    // meanwhile.
                    setReady(true);
                } else {
                    await decodeSampleClip(sourceUrl);
                }
            } else {
                // Peaks unavailable for a full track. Don't fall back to a
                // no-peaks WaveSurfer — that makes it download+decode the whole
                // file itself (minutes on a throttled source). Reveal the
                // click-seekable area instead.
                setReady(true);
            }
        })();

        return () => {
            cancelled = true;
            window.removeEventListener('player-sample-fallback', onSampleFallback);
            if (offLazyFallback) window.removeEventListener('lazy-audio-fallback', offLazyFallback);
            clearTimeout(timeoutRef.current);
            if (wavesurferRef.current) {
                try {
                    wavesurferRef.current.unAll();
                    wavesurferRef.current.destroy();
                } catch (err) { /* ignore */ }
                wavesurferRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceUrl, originalUrl]);

    return (
        <div className="waveform-overlay">
            {!ready && (
                <div className="waveform-loader">
                    <div className="waveform-loader-bar" />
                </div>
            )}
            <div
                className="waveform-container"
                ref={waveformRef}
                onClick={handleClick}
                style={{ opacity: ready ? 1 : 0, cursor: 'pointer' }}
            ></div>
        </div>
    );
};

export default WaveformOverlay;
